import {useCallback} from 'react';

import useEventBarFastFollows from '@/components/global/layout/AppBar/hooks/useEventBarFastFollows';
import {useGlobalFulfillmentDetail} from '@/components/global/providers/GlobalFulfillmentDetailProvider';
import {useEventBarV2} from '../EventBarV2Provider';
import {buildEventBarSeedFromFulfillmentDetail} from '../utils/fulfillment';
import {searchHistoryAddressToGeocoded} from '../utils/where';
import {useCommitEventBarFulfillment} from './useCommitEventBarFulfillment';
import {type EventBarSearchHistoryEntry} from './useEventBarSearchHistory';
import {useEventBarV2Search} from './useEventBarV2Search';

const useApplySearchHistoryEntry = (): ((entry: EventBarSearchHistoryEntry) => Promise<void>) => {
  const {
    setWhereSelection,
    setWhereValue,
    setFulfillmentType,
    setWhenDate,
    setWhenTime,
    setHeadCount,
    setEventName,
    hasUserEditedWhereRef,
  } = useEventBarV2();
  const {commitFulfillmentValues} = useCommitEventBarFulfillment();
  const {submitSearch, runSearchSubmission} = useEventBarV2Search();
  const {fulfillmentDetail} = useGlobalFulfillmentDetail();
  const {showEventBarFastFollows} = useEventBarFastFollows();

  return useCallback(
    async (entry: EventBarSearchHistoryEntry) => {
      const {address} = entry;
      const whereSelection = {
        kind: 'geocoded',
        geocoded: searchHistoryAddressToGeocoded(address),
      } as const;
      const seed = buildEventBarSeedFromFulfillmentDetail(entry);

      // Reflect the restored search in the bar UI — on every page, so the bar never keeps stale
      // staged values from before the click (e.g. a headcount typed on the homepage).
      hasUserEditedWhereRef.current = true;
      setWhereSelection(whereSelection);
      setWhereValue(address.fullAddress || entry.addressLabel);
      setFulfillmentType(seed.strategy);
      setWhenDate(seed.whenDate);
      setWhenTime(seed.whenTime);
      setHeadCount(seed.headCount);
      setEventName(seed.eventName);

      // ezx584 treatment: the entry is itself a persisted detail, so search under it rather than
      // copying its values onto the current detail, which would overwrite that detail's own search.
      // On the search page, the submission keeps the results loading until the new page lands.
      if (showEventBarFastFollows) {
        await runSearchSubmission(() => submitSearch(undefined, entry.fulfillmentDetailId));
        return;
      }

      // No active detail to update on this page (home, start-new-order, …). The entry is itself a
      // persisted detail, so search under it directly rather than committing to — or creating — one.
      if (!fulfillmentDetail) {
        submitSearch(undefined, entry.fulfillmentDetailId);
        return;
      }

      // Persist the restored values, then run the search — committing from the entry (not the state
      // set above, which hasn't rendered) so the search reflects the restored fulfillment detail.
      // Run as one submission so the commit and the search refetch results once, not twice.
      await runSearchSubmission(async () => {
        await commitFulfillmentValues({
          whereSelection,
          fulfillmentType: seed.strategy,
          whenDate: seed.whenDate,
          whenTime: seed.whenTime,
          headCount: seed.headCount,
          eventName: seed.eventName,
        });
        await submitSearch();
      });
    },
    [
      setWhereSelection,
      setWhereValue,
      setFulfillmentType,
      setWhenDate,
      setWhenTime,
      setHeadCount,
      setEventName,
      hasUserEditedWhereRef,
      commitFulfillmentValues,
      submitSearch,
      runSearchSubmission,
      fulfillmentDetail,
      showEventBarFastFollows,
    ],
  );
};

export default useApplySearchHistoryEntry;
